// ============================================================================
// PARTICIPANT SERVICE
// API client for the mobile participant view.
// Uses HttpOnly session cookie after claim — no Authorization header needed.
// ============================================================================

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const TOKEN_KEY = 'participant_token';

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = sessionStorage.getItem(TOKEN_KEY);
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  Object.assign(headers, options.headers || {});
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers,
    ...options,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw Object.assign(new Error(body.error || `HTTP ${res.status}`), { status: res.status, body });
  }
  return res.json();
}

export interface JoinInfo {
  participantId: string;
  sessionId: string;
  sessionName: string;
  claimed: boolean;
}

export interface ParticipantAvatar {
  id: string;
  name: string;
  role: string;
  status: string;
  imageUrl?: string;
  primaryColor?: string;
}

export interface PendingContributionItem {
  role: string;
  avatarName: string;
  avatarImageUrl?: string;
  avatarPrimaryColor?: string;
  text: string;
  reason?: string;
  urgency?: 'urgent' | 'important' | 'normal';
  audioUrl?: string;
}

export interface PublishedOutput {
  id: string;
  title: string;
  type: string;
  body: string;
  url?: string;
  publishedAt: string;
}

export interface PublishedAsset {
  id: string;
  name: string;
  type: string;
  url: string;
  publishedAt: string;
}

export interface ActiveWidget {
  widgetId: string;
  type: 'mcq' | 'free_text' | 'rating';
  question: string;
  options?: string[];
  ratingMax?: number;
  submittedAnswer?: string | null;
}

// ── Tabletop seat-awareness (Phase 4) ───────────────────────────────────────
// Mirrors backend/src/services/tabletop-second-screen.service.ts's
// ParticipantTabletopView. Present (non-null) only for sessionMode ===
// 'tabletop' sessions with a linked exercise; standard sessions get null.

export interface TabletopSeatInfo {
  personaId: string;
  displayName: string;
  category: string;
  authority?: string;
}

export interface TabletopOpenSeat {
  personaId: string;
  displayName: string;
  category: string;
}

export interface TabletopBrief {
  knows: string[];
  suspects: string[];
  wrongBeliefs: string[];
}

export interface TabletopInboxItem {
  seq: number;
  content: string;
  simTimeMs: number;
}

export interface TabletopDecisionItem {
  text: string;
  type: string;
  calledParty?: string;
  simTimeMs: number;
}

export interface TabletopPublicArtefact {
  artefactId: string;
  kind: string;
  url?: string;
  text?: string;
  lowerThird?: string;
}

export interface TabletopCoachingCard {
  personaId: string;
  strengths: string[];
  developmentAreas: string[];
}

export interface TabletopCastContact {
  personaId: string;
  displayName: string;
  category: string;
  organisation?: string;
  deliveryChannel: string;
}

export interface TabletopChatMessage {
  text: string;
  speaker: 'human' | 'avatar';
  simTimeMs: number;
}

export interface TabletopChatThread {
  personaId: string;
  displayName: string;
  messages: TabletopChatMessage[];
}

export interface ParticipantTabletopState {
  seat: TabletopSeatInfo | null;
  openSeats: TabletopOpenSeat[];
  brief: TabletopBrief | null;
  inbox: TabletopInboxItem[];
  decisions: TabletopDecisionItem[];
  publicArtefacts: TabletopPublicArtefact[];
  runStatus: 'running' | 'held' | 'complete' | 'not_started';
  actLabel?: string;
  coachingCard?: TabletopCoachingCard | null;
  castContacts: TabletopCastContact[];
  chatThreads: Record<string, TabletopChatThread>;
}

export interface SessionState {
  sessionName: string;
  phase: string;
  phaseIndex: number;
  totalPhases: number;
  avatars: ParticipantAvatar[];
  pendingContributions: PendingContributionItem[];
  publishedOutputs: PublishedOutput[];
  publishedAssets: PublishedAsset[];
  activeWidget: ActiveWidget | null;
  reachy: { available: boolean; busy: boolean };
  guide: { name: string; enabled: boolean };
  tabletop: ParticipantTabletopState | null;
}

// Mirrors tabletopChatThreadKey in backend/src/services/tabletop-persona-chat.service.ts —
// must stay byte-identical since the backend keys chatThreads by this same value.
export function tabletopChatThreadKey(personaIdA: string, personaIdB: string): string {
  return [personaIdA, personaIdB].sort().join('::');
}

export const participantService = {
  async getJoinInfo(token: string): Promise<JoinInfo> {
    return request(`/api/participant/join?token=${encodeURIComponent(token)}`);
  },

  async join(token: string, name: string): Promise<{ success: boolean; sessionId: string; name: string }> {
    const result = await request<{ success: boolean; sessionId: string; name: string; token?: string }>(
      '/api/participant/join',
      { method: 'POST', body: JSON.stringify({ token, name }) }
    );
    if (result.token) sessionStorage.setItem(TOKEN_KEY, result.token);
    return result;
  },

  async getState(sessionId: string): Promise<SessionState> {
    return request(`/api/participant/${sessionId}/state`);
  },

  async sendChat(sessionId: string, message: string, avatarId?: string): Promise<{ messageId: string; response: string }> {
    return request(`/api/participant/${sessionId}/chat`, {
      method: 'POST',
      body: JSON.stringify({ message, avatarId }),
    });
  },

  async converse(sessionId: string): Promise<{ success: boolean }> {
    return request(`/api/participant/${sessionId}/converse`, { method: 'POST' });
  },

  async submitWidgetResponse(sessionId: string, widgetId: string, answer: string): Promise<{ success: boolean }> {
    return request(`/api/participant/${sessionId}/widget-response`, {
      method: 'POST',
      body: JSON.stringify({ widgetId, answer }),
    });
  },

  async claimTabletopSeat(sessionId: string, personaId: string): Promise<{ persona: TabletopSeatInfo }> {
    return request(`/api/participant/${sessionId}/tabletop/claim-seat`, {
      method: 'POST',
      body: JSON.stringify({ personaId }),
    });
  },

  async submitTabletopDecision(
    sessionId: string,
    input: { text: string; type: 'decision' | 'call_log'; calledParty?: string }
  ): Promise<{ success: boolean }> {
    return request(`/api/participant/${sessionId}/tabletop/decisions`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  async summonTabletopPersona(sessionId: string, personaId: string, prompt?: string): Promise<{ success: boolean }> {
    return request(`/api/participant/${sessionId}/tabletop/summon`, {
      method: 'POST',
      body: JSON.stringify({ personaId, prompt }),
    });
  },
};
